import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import ActivityCatalogService from "./ActivityCatalogService";

/**
 * Debounced fuzzy-search dropdown for the activity catalog. Same shape as
 * medicines/MedicineCatalogAutocomplete but wired to api-activity-v2-service's
 * `/api/v1/activity-catalog` endpoint.
 *
 * On pick, hands the parent a snapshot of the fields the backend stores as
 * catalog snapshots (name + category + catalogId). Typing without picking is
 * allowed so the query stays visible, but the catalog identifier is cleared
 * — the parent's Save gate insists on a real catalog pick, so free-typed
 * activities can never reach the wire.
 */
const DEBOUNCE_MS = 250;

/* Board 3071:4314 — the board moves the magnifier to the RIGHT of the field
   and draws it as a 16px Material `search` glyph in #1c1b1f, where the shared
   `.sec-search` puts a 1.6-stroke outline circle on the LEFT. Material Icons
   is not loaded in this app, hence the inline path. */
const SearchIcon = () => (
  <svg
    className="pa3-ac-search"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M13.0667 14L8.86667 9.8C8.53333 10.0667 8.15 10.2778 7.71667 10.4333C7.28333 10.5889 6.82222 10.6667 6.33333 10.6667C5.12222 10.6667 4.09722 10.2472 3.25833 9.40833C2.41944 8.56944 2 7.54444 2 6.33333C2 5.12222 2.41944 4.09722 3.25833 3.25833C4.09722 2.41944 5.12222 2 6.33333 2C7.54444 2 8.56944 2.41944 9.40833 3.25833C10.2472 4.09722 10.6667 5.12222 10.6667 6.33333C10.6667 6.82222 10.5889 7.28333 10.4333 7.71667C10.2778 8.15 10.0667 8.53333 9.8 8.86667L14 13.0667L13.0667 14ZM6.33333 9.33333C7.16667 9.33333 7.875 9.04167 8.45833 8.45833C9.04167 7.875 9.33333 7.16667 9.33333 6.33333C9.33333 5.5 9.04167 4.79167 8.45833 4.20833C7.875 3.625 7.16667 3.33333 6.33333 3.33333C5.5 3.33333 4.79167 3.625 4.20833 4.20833C3.625 4.79167 3.33333 5.5 3.33333 6.33333C3.33333 7.16667 3.625 7.875 4.20833 8.45833C4.79167 9.04167 5.5 9.33333 6.33333 9.33333Z" />
  </svg>
);

const dedupeByID = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = (r && r.id) || (r && r.name) || "";
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

const ActivityCatalogAutocomplete = ({
  value,
  catalogId,
  onChange,
  disabled,
  isAlreadyActive,
}) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const serviceRef = useRef(null);

  if (!serviceRef.current) serviceRef.current = new ActivityCatalogService();

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Kick off an initial search when the input is focused with an empty query
  // — matches the mock's dropdown behaviour (opens with the first 20 alpha
  // entries even before the user types).
  const openDropdown = () => {
    setOpen(true);
    if (results.length === 0 && !loading) fetchResults("");
  };

  // fetchResults now accepts an `openWhenDone` flag so the mount-time
  // prefetch below can silently warm the cache without popping the menu
  // open before the reviewer has touched the field. Live search paths
  // still open the menu on response.
  const fetchResults = (q, { openWhenDone = true } = {}) => {
    setLoading(true);
    serviceRef.current.search(
      q,
      (data) => {
        setResults(dedupeByID(Array.isArray(data) ? data : []));
        setLoading(false);
        if (openWhenDone) setOpen(true);
      },
      () => {
        setResults([]);
        setLoading(false);
      }
    );
  };

  // Mount-time prefetch: when the parent seeds a `value` (typically the
  // transcript flow's AI-populated activity name), warm the results so
  // the FIRST focus shows the dropdown immediately. If the AI's string
  // exactly matches one catalog row (case-insensitive), auto-pick it —
  // the reviewer never has to click.
  const didInitialSearch = useRef(false);
  useEffect(() => {
    if (didInitialSearch.current) return;
    const seed = (value || "").trim();
    if (!seed) return;
    // Skip when the parent already handed us a catalog id (a downstream
    // click-to-edit already resolved the pick — no need to re-fire).
    if (catalogId) return;
    didInitialSearch.current = true;
    setLoading(true);
    serviceRef.current.search(
      seed,
      (data) => {
        const rows = dedupeByID(Array.isArray(data) ? data : []);
        setResults(rows);
        setLoading(false);
        const exact = rows.find(
          (r) => (r.name || "").trim().toLowerCase() === seed.toLowerCase()
        );
        if (exact) {
          // Skip auto-pick when the exact match is already-active on
          // the patient — clicking it would silently no-op, and
          // auto-picking silently would drop the reviewer into a
          // dead-end pick. Leave the row surfaced in the dropdown
          // instead so the "already added" tag can explain.
          const blocked = isAlreadyActive ? !!isAlreadyActive(exact) : false;
          if (blocked) return;
          onChange({
            name: exact.name || "",
            catalogId: exact.id || "",
            category: exact.category || "",
          });
        }
      },
      () => {
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Free-text edit drops the catalog link — parent's catalogId/category
    // are cleared so the row saves as free-text.
    onChange({ name: q, catalogId: "", category: "" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(q), DEBOUNCE_MS);
  };

  const pick = (item) => {
    setQuery(item.name || "");
    setOpen(false);
    onChange({
      name: item.name || "",
      catalogId: item.id || "",
      category: item.category || "",
    });
  };

  return (
    /* `.cc-icd*` / `.sec-search` are shared by seven autocompletes (ICD,
       medicines, procedures, ingredients, lab tests…), so board 3071:4437 is
       applied through a `.pa3-ac` hook rather than edited in place. */
    <div className="cc-icd pa3-ac" ref={boxRef}>
      <div className="sec-search">
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={openDropdown}
          placeholder="Search physical activity…"
          disabled={disabled}
        />
        <SearchIcon />
      </div>
      {open && (
        <ul className="cc-icd-menu" role="listbox">
          {loading && <li className="cc-icd-empty">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="cc-icd-empty">
              No matches — refine the search to pick from the list.
            </li>
          )}
          {!loading &&
            results.map((r) => {
              const alreadyActive = isAlreadyActive ? !!isAlreadyActive(r) : false;
              return (
                <li
                  key={r.id || r.name}
                  role="option"
                  aria-selected={catalogId && r.id === catalogId}
                  aria-disabled={alreadyActive}
                  className={`cc-icd-opt${alreadyActive ? " is-already-active" : ""}`}
                  onClick={() => {
                    // Silent no-op — the greyed row + pill tell the reviewer
                    // why. Update the row from the section table instead.
                    if (alreadyActive) return;
                    pick(r);
                  }}
                  title={
                    alreadyActive
                      ? "Already in the physical-activity log — update it there instead"
                      : undefined
                  }
                >
                  <span className="cc-icd-name">{r.name}</span>
                  {/* Right-side stack: category chip on top, state pill
                      (already added) directly beneath. Same layout the
                      medicine + allergen pickers use. */}
                  <span className="cc-icd-side">
                    {r.category && (
                      /* Board 3071:4440 — the category is a bordered white chip
                         led by a 6px #f97f0f dot, not a bare tinted tag. */
                      <span className="cc-icd-tag">
                        <span className="pa3-ac-dot" aria-hidden="true" />
                        {r.category}
                      </span>
                    )}
                    {alreadyActive && (
                      <span className="cc-icd-active-tag" aria-hidden="true">
                        already added
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

ActivityCatalogAutocomplete.propTypes = {
  value: PropTypes.string,
  catalogId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  /** Predicate (row → bool). When true the option renders greyed with an
      "already added" pill and clicks silently no-op. */
  isAlreadyActive: PropTypes.func,
};

export default ActivityCatalogAutocomplete;
